import {
    AccountingRuleActions,
    RuleActionType,
} from "../types/transactionRule";
import { omitRuleActions } from "./transactionRuleActions";
import {
    InvoiceOrBillMatchFields,
    isMatchedToInvoiceOrBill,
} from "./transactions";

/**
 * Fields copied from an invoice/bill onto the matched transaction, and
 * locked on that transaction until the match is removed.
 *
 * Copy and lock are the same list. Match-time writers
 * (accrual-review split / counterparty sync) and the post-match lock
 * (MatchedTransactionFieldLockedError + UI disable) both read from here —
 * do not add a field to one path without the other.
 */
export const MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP = {
    counterparty: {
        lockLabel: "counterparty",
        errorField: "counterparty",
    },
    classes: {
        lockLabel: "class",
        errorField: "classes",
    },
    cashAccount: {
        lockLabel: "cash ledger",
        errorField: "accountId",
    },
    accrualAccount: {
        lockLabel: "accrual ledger",
        errorField: "accrualAccountId",
    },
} as const;

export type MatchedDocumentOwnedField =
    keyof typeof MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP;

export type MatchedDocumentLockErrorField =
    (typeof MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP)[MatchedDocumentOwnedField]["errorField"];

export const MATCHED_DOCUMENT_LOCK_LABEL_BY_ERROR_FIELD = {
    [MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.cashAccount.errorField]:
        MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.cashAccount.lockLabel,
    [MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.accrualAccount.errorField]:
        MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.accrualAccount.lockLabel,
    [MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.classes.errorField]:
        MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.classes.lockLabel,
    [MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.counterparty.errorField]:
        MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP.counterparty.lockLabel,
} as const satisfies Record<MatchedDocumentLockErrorField, string>;

/**
 * Transaction-update payload keys that write an owned field. Used to decide
 * whether the matched-document lock lookups are worth running.
 */
export interface MatchedDocumentOwnedFieldUpdate {
    counterparty?: { id: string } | null;
    assignedClasses?: Array<{ id: string }>;
    account?: { id: string } | null;
    accrualAccount?: { id: string } | null;
    suggestedAccrualAccount?: { id: string } | null;
    accountOverrides?: Array<{ accountingLedgerId: string }>;
}

const MATCHED_DOCUMENT_SUPPRESSED_RULE_ACTION_TYPES = new Set<RuleActionType>([
    ...AccountingRuleActions,
    RuleActionType.CLASS,
    RuleActionType.COUNTERPARTY,
]);

export function isMatchedDocumentOwnedFieldUpdate(
    update: MatchedDocumentOwnedFieldUpdate,
): boolean {
    return (
        update.counterparty !== undefined ||
        update.assignedClasses !== undefined ||
        update.account !== undefined ||
        update.accrualAccount !== undefined ||
        update.suggestedAccrualAccount !== undefined ||
        (update.accountOverrides?.length ?? 0) > 0
    );
}

/**
 * Drops rule actions that write a matched-document-owned field. A rule with
 * no actions left is removed so it is not recorded as applied.
 */
export function omitMatchedDocumentOwnedRuleActions<
    TRule extends { actions: Array<{ actionType: RuleActionType }> },
>(rules: readonly TRule[]): TRule[] {
    return omitRuleActions(
        rules,
        MATCHED_DOCUMENT_SUPPRESSED_RULE_ACTION_TYPES,
    );
}

export const MATCHED_DOCUMENT_FIELD_LOCK_TOOLTIP =
    "Set by the matched invoice or bill. Unmatch to change it.";

export function getMatchedDocumentFieldLock({
    matchLists,
    isOtherwiseLocked = false,
}: {
    matchLists: Array<InvoiceOrBillMatchFields[] | null | undefined>;
    isOtherwiseLocked?: boolean;
}): { isLocked: boolean; tooltip: string | undefined } {
    const isLocked = isMatchedToInvoiceOrBill(...matchLists);
    return {
        isLocked,
        tooltip:
            isLocked && !isOtherwiseLocked
                ? MATCHED_DOCUMENT_FIELD_LOCK_TOOLTIP
                : undefined,
    };
}
